import type {
	LotMatchPartner,
	MatchPriceInformation,
	PopMatchPartner,
	TransportCostResponse
} from '$mapapp/data/data.model'
import { CalcMethod, LotType } from '$types/enums'
import { roundToNearest, Unit } from '$utils/formatters'

export enum PayType {
	IS_PAYING,
	IS_BEING_PAID
}

type PriceAndType = {
	price: string // the rounded string including currency
	type: PayType // if the price is being paid or the entity is paying
}

export type MatchingPrices = {
	reference: PriceAndType // the reference price (budget or planned revenue) -> defines the sign (+/-)
	transport: string // the transport cost always needs to be paid
	partner: PriceAndType // the partner price (either being paid - negative or paying the reference - positive, since we view the calculation from the perspective of the reference)
	total: { value: number | null; formatted: string } // the total of the partner and transport cost
	delta: { value: number | null; formatted: string } // the difference between the reference and the total
}

export const PAID_BY_REFERENCE_TYPES = [LotType.MATERIAL_NEED, LotType.DISPOSAL]

export function getTransportCost(transportCostResponse: TransportCostResponse) {
	return (
		transportCostResponse?.vehicleTransportCosts?.[0]?.costPerTonAndDistance ||
		transportCostResponse?.flatrateTransportCosts
	)
}

function roundWithEuro(value: number) {
	return roundToNearest(value, Unit.EUR_PER_TON)
}

export const NO_PRICE_GIVEN = 'n.a.' // TODO JB: string resource

/**
 * This function returns the reference price and if the reference is paying or being paid.
 * Depending on the reference type, we show a '+' or a '-' sign for the budgeted price.
 * If we return 'IS_PAYING', we need to show '-', if we return 'IS_BEING_PAID', we need to show '+'.
 * @param reference the reference object
 * @param referencePrice the reference price
 */
function getReferencePriceAndType(reference: LotMatchPartner, referencePrice: number) {
	if (reference.calcMethod === CalcMethod.REQUEST_OFFER) {
		return {
			price: NO_PRICE_GIVEN,
			type: PAID_BY_REFERENCE_TYPES.includes(reference.type)
				? PayType.IS_PAYING
				: PayType.IS_BEING_PAID
		}
	}

	return {
		price: roundWithEuro(referencePrice),
		type: PAID_BY_REFERENCE_TYPES.includes(reference.type)
			? PayType.IS_PAYING
			: PayType.IS_BEING_PAID
	}
}

/**
 * This function returns the partner price and if the partner is paying or being paid.
 * Depending on the partner type, we show a '+' or a '-' sign for the actual cost/revenue.
 * If we return 'IS_PAYING' (the reference), we need to show '+', if we return 'IS_BEING_PAID' (by the reference), we need to show '-'.
 * @param partner
 * @param partnerPrice
 */
function getPartnerPriceAndType(partner: LotMatchPartner | PopMatchPartner, partnerPrice: number) {
	// a pop is always being paid
	if (partner.objType === 'pop') {
		if (!partnerPrice) {
			return {
				price: NO_PRICE_GIVEN,
				type: PayType.IS_BEING_PAID
			}
		}

		return {
			price: roundWithEuro(partnerPrice),
			type: PayType.IS_BEING_PAID
		}
	}

	partner = partner as LotMatchPartner
	const type = PAID_BY_REFERENCE_TYPES.includes(partner.type)
		? PayType.IS_PAYING
		: PayType.IS_BEING_PAID
	if (partner.calcMethod === CalcMethod.REQUEST_OFFER) {
		return {
			price: NO_PRICE_GIVEN,
			type
		}
	}
	return {
		price: roundWithEuro(partnerPrice),
		type
	}
}

function calculatePricesForPop(
	_reference: PopMatchPartner,
	partner: LotMatchPartner,
	priceInformation: MatchPriceInformation
): MatchingPrices {

	const transportCost = getTransportCost(priceInformation.transportCost) ?? 0
	const formattedTransportCost = roundWithEuro(transportCost)

	const referencePrice = priceInformation.pricePerTon ?? 0
	const formattedReferencePrice = {
		price: referencePrice > 0 ? roundWithEuro(referencePrice) : NO_PRICE_GIVEN,
		type: PayType.IS_BEING_PAID
	}

	const partnerPrice = parseFloat(priceInformation.partnerPricePerTon) || 0
	const formattedPartnerPrice = getPartnerPriceAndType(partner, partnerPrice)

	if (partner.calcMethod === CalcMethod.REQUEST_OFFER) {
		return {
			reference: formattedReferencePrice,
			transport: formattedTransportCost,
			partner: formattedPartnerPrice,
			total: { value: null, formatted: NO_PRICE_GIVEN },
			delta: { value: null, formatted: NO_PRICE_GIVEN }
		}
	}

	let total
	if (PAID_BY_REFERENCE_TYPES.includes((partner as LotMatchPartner).type)) {
		total = (transportCost * -1) + partnerPrice
	} else {
		total = (transportCost * -1) - partnerPrice
	}
	const delta = total - referencePrice

	return {
		reference: formattedReferencePrice,
		transport: formattedTransportCost,
		partner: formattedPartnerPrice,
		total: { value: total, formatted: roundWithEuro(Math.abs(total)) },
		delta: { value: delta, formatted: roundWithEuro(delta) }
	}
}

function calculatePricesForLot(
	reference: LotMatchPartner,
	partner: LotMatchPartner | PopMatchPartner,
	priceInformation: MatchPriceInformation
): MatchingPrices {
	const referencePrice = priceInformation.pricePerTon ?? 0
	const formattedReferencePrice = getReferencePriceAndType(reference, referencePrice)

	const partnerPrice = parseFloat(priceInformation.partnerPricePerTon) || 0
	const formattedPartnerPrice = getPartnerPriceAndType(partner, partnerPrice)

	const transportCost = getTransportCost(priceInformation.transportCost) ?? 0
	const formattedTransportCost = roundWithEuro(transportCost)

	// special case where partner has no price -> all the calculations do not really make sense, hence we return 'n.a'
	// TODO: at the moment, we cannot differentiate between a pop actually having price 0 and a pop that has no price (backend returns 0 in both cases)
	if (
		(partner.objType === 'lot' &&
			(partner as LotMatchPartner).calcMethod === CalcMethod.REQUEST_OFFER) ||
		(partner.objType === 'pop' && partnerPrice === 0)
	) {
		return {
			reference: formattedReferencePrice,
			transport: formattedTransportCost,
			partner: formattedPartnerPrice,
			total: { value: null, formatted: NO_PRICE_GIVEN },
			delta: { value: null, formatted: NO_PRICE_GIVEN }
		}
	}

	let total
	if (
		partner.objType === 'lot' &&
		PAID_BY_REFERENCE_TYPES.includes((partner as LotMatchPartner).type)
	) {
		total = transportCost * -1 + partnerPrice
	} else {
		total = transportCost * -1 - partnerPrice
	}

	const delta = total + referencePrice

// if the reference is a request offer or has no price, we can still return the total
	if (formattedReferencePrice.price === NO_PRICE_GIVEN) {
		return {
			reference: formattedReferencePrice,
			transport: formattedTransportCost,
			partner: formattedPartnerPrice,
			total: { value: total, formatted: roundWithEuro(Math.abs(total)) },
			delta: { value: null, formatted: NO_PRICE_GIVEN }
		}
	}

	return {
		reference: formattedReferencePrice,
		transport: formattedTransportCost,
		partner: formattedPartnerPrice,
		total: { value: total, formatted: roundWithEuro(Math.abs(total)) },
		delta: { value: delta, formatted: roundWithEuro(delta) }
	}
}

export function calculateMatchingPrices(
	reference: LotMatchPartner | PopMatchPartner,
	partner: LotMatchPartner | PopMatchPartner,
	priceInformation: MatchPriceInformation
) {
	if (reference.objType === 'pop') {
		return calculatePricesForPop(
			reference as PopMatchPartner,
			partner as LotMatchPartner,
			priceInformation
		)
	}

	return calculatePricesForLot(reference as LotMatchPartner, partner, priceInformation)
}
